"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.requiredSignalsHavePins = requiredSignalsHavePins;
exports.modelPinoutCoversSignals = modelPinoutCoversSignals;
exports.missingRequiredParameterNames = missingRequiredParameterNames;
exports.requiredParametersSet = requiredParametersSet;
exports.arrayPinoutComplete = arrayPinoutComplete;
exports.arrayFormCompletion = arrayFormCompletion;
exports.repeatableBlockPinoutComplete = repeatableBlockPinoutComplete;
exports.repeatableBlockFormCompletion = repeatableBlockFormCompletion;
exports.simpleFormCompletion = simpleFormCompletion;
const no_connect_signal_1 = require("./no-connect-signal");
function normalizePins(pins) {
    return pins.map((pin) => pin.trim()).filter((pin) => pin !== '');
}
function pinsForSignal(pinout, signalId) {
    return normalizePins(pinout[signalId] ?? []);
}
function assignedFunctionalDesignators(pinout) {
    const designators = new Set();
    for (const [signalId, pins] of Object.entries(pinout)) {
        if (signalId === no_connect_signal_1.NO_CONNECT_SIGNAL_ID) {
            continue;
        }
        for (const pin of normalizePins(pins)) {
            designators.add(pin);
        }
    }
    return designators;
}
function noConnectPinsOverlap(pinout, functionalDesignators) {
    return pinsForSignal(pinout, no_connect_signal_1.NO_CONNECT_SIGNAL_ID).some((pin) => functionalDesignators.has(pin));
}
function requiredSignalsHavePins(modelSignals, pinout) {
    return modelSignals
        .filter((signal) => !signal.isOptional)
        .every((signal) => pinsForSignal(pinout, String(signal.id)).length > 0);
}
function modelPinoutCoversSignals(modelSignals, pinout, formSignals = []) {
    if (!requiredSignalsHavePins(modelSignals, pinout)) {
        return false;
    }
    const activeDesignators = new Set();
    for (const signal of modelSignals) {
        const pins = pinsForSignal(pinout, String(signal.id));
        if (signal.isOptional && pins.length === 0) {
            continue;
        }
        for (const pin of pins) {
            activeDesignators.add(pin);
        }
    }
    const modelSignalIds = new Set(modelSignals.map((signal) => String(signal.id)));
    for (const signal of formSignals) {
        const signalId = String(signal.id);
        if (signalId === no_connect_signal_1.NO_CONNECT_SIGNAL_ID || modelSignalIds.has(signalId)) {
            continue;
        }
        for (const pin of pinsForSignal(pinout, signalId)) {
            activeDesignators.add(pin);
        }
    }
    return (activeDesignators.size === assignedFunctionalDesignators(pinout).size &&
        !noConnectPinsOverlap(pinout, activeDesignators));
}
function missingRequiredParameterNames(modelParameters, parameters) {
    return modelParameters
        .filter((parameter) => parameter.required)
        .filter((parameter) => {
        const entry = parameters[parameter.name];
        return entry?.notSpecified !== true && (entry?.value?.trim() ?? '') === '';
    })
        .map((parameter) => parameter.name);
}
function requiredParametersSet(modelParameters, parameters) {
    return missingRequiredParameterNames(modelParameters, parameters).length === 0;
}
function arrayPinoutComplete(args) {
    const { formSignals, pinout, elementSignals, elementCount, submodelPrefix } = args;
    const requiredSignalNames = new Set();
    const optionalSignalNames = new Set();
    for (let index = 1; index <= elementCount; index += 1) {
        const subModelId = `${submodelPrefix}${index}`;
        for (const elementSignal of elementSignals) {
            const compositeName = `${subModelId}_${elementSignal.name}`;
            (elementSignal.isOptional ? optionalSignalNames : requiredSignalNames).add(compositeName);
        }
    }
    const signalsByName = new Map(formSignals.map((signal) => [signal.name, signal]));
    for (const requiredName of requiredSignalNames) {
        const signal = signalsByName.get(requiredName);
        if (signal === undefined) {
            return false;
        }
        if (pinsForSignal(pinout, String(signal.id)).length === 0) {
            return false;
        }
    }
    const activeDesignators = new Set();
    for (const signal of formSignals) {
        const signalId = String(signal.id);
        if (signalId === no_connect_signal_1.NO_CONNECT_SIGNAL_ID) {
            continue;
        }
        const pins = pinsForSignal(pinout, signalId);
        if (optionalSignalNames.has(signal.name) && pins.length === 0) {
            continue;
        }
        for (const pin of pins) {
            activeDesignators.add(pin);
        }
    }
    return (activeDesignators.size === assignedFunctionalDesignators(pinout).size &&
        !noConnectPinsOverlap(pinout, activeDesignators));
}
function arrayFormCompletion(args) {
    const { elementShape } = args;
    if (elementShape === null) {
        return { pinoutComplete: false, requiredParamsSet: false };
    }
    return {
        pinoutComplete: arrayPinoutComplete({
            formSignals: args.formSignals,
            pinout: args.pinout,
            elementSignals: elementShape.signals,
            elementCount: args.elementCount,
            submodelPrefix: args.submodelPrefix,
        }),
        requiredParamsSet: requiredParametersSet(elementShape.parameters, args.parameters),
    };
}
function repeatableBlockPinoutComplete(args) {
    const { formSignals, pinout, modelSignals } = args;
    const optionalByModelSignalId = new Map(modelSignals.map((signal) => [String(signal.id), signal.isOptional]));
    const packageSignalShapes = formSignals.map((signal) => ({
        id: signal.id,
        name: signal.name,
        isOptional: optionalByModelSignalId.get(String(signal.modelSignal ?? '')) ?? false,
    }));
    return modelPinoutCoversSignals(packageSignalShapes, pinout);
}
function repeatableBlockFormCompletion(args) {
    const { modelShape } = args;
    if (modelShape === null) {
        return { pinoutComplete: false, requiredParamsSet: false };
    }
    return {
        pinoutComplete: repeatableBlockPinoutComplete({
            formSignals: args.formSignals,
            pinout: args.pinout,
            modelSignals: modelShape.signals,
        }),
        requiredParamsSet: requiredParametersSet(modelShape.parameters, args.parameters),
    };
}
function simpleFormCompletion(args) {
    const { modelShape } = args;
    if (modelShape === null) {
        return { pinoutComplete: false, requiredParamsSet: false };
    }
    return {
        pinoutComplete: modelPinoutCoversSignals(modelShape.signals, args.pinout, args.formSignals),
        requiredParamsSet: requiredParametersSet(modelShape.parameters, args.parameters),
    };
}
//# sourceMappingURL=form-completion-rules.js.map